import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Send, Loader2, AlertCircle, CheckCircle2, Edit3, Home, Zap, Layers, Rocket } from 'lucide-react';

const WorkflowGeneratorPage = () => {
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [workflowSuggestions, setWorkflowSuggestions] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [error, setError] = useState('');

  const examplePrompts = [
    "Email me a summary of my Gmail messages every morning at 8am",
    "When I get a Slack message containing 'urgent', email me and post to #alerts",
    "Save new Stripe payments to Google Sheets and send receipt email",
    "Post new GitHub issues to our Discord #dev-updates channel",
    "Send me a Slack notification when my website goes down"
  ];

  const handleGenerate = async () => {
    if (!description.trim()) {
      setError('Please enter a workflow description');
      return;
    }

    setLoading(true);
    setError('');
    setWorkflowSuggestions([]);
    setSelectedWorkflow(null);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const response = await axios.post(
        `${API_URL}/workflows/generate`,
        {
          description: description,
          user_context: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success && response.data.workflows) {
        setWorkflowSuggestions(response.data.workflows);
      } else {
        setError('Failed to generate workflow suggestions');
      }

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate workflow. Please try again.');
      console.error('Generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
  };

  const handleSaveDraft = async (workflow) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      await axios.post(
        `${API_URL}/workflows/generate/save-draft`,
        {
          workflow_data: workflow.workflow,
          log_id: workflow.log_id
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Record approval feedback
      await axios.post(
        `${API_URL}/workflows/generate/feedback`,
        {
          log_id: workflow.log_id,
          approved: true
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      alert('Workflow saved as draft! You can edit and activate it from the workflows page.');
      navigate('/workflows');

    } catch (err) {
      setError('Failed to save workflow');
      console.error('Save error:', err);
    }
  };

  const handleEdit = (workflow) => {
    // Navigate to workflow builder with pre-filled data
    navigate('/workflows/builder', { state: { generatedWorkflow: workflow.workflow } });
  };

  const getConfidenceColor = (conf) => {
    if (conf >= 0.8) return 'text-green-600 bg-green-50 border-green-200';
    if (conf >= 0.6) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getConfidenceLabel = (conf) => {
    if (conf >= 0.8) return 'High Confidence';
    if (conf >= 0.6) return 'Medium Confidence';
    return 'Low Confidence';
  };

  const getApproachIcon = (index) => {
    const icons = [
      <Zap className="w-6 h-6" />,
      <Layers className="w-6 h-6" />,
      <Rocket className="w-6 h-6" />
    ];
    return icons[index] || <Sparkles className="w-6 h-6" />;
  };

  const getApproachColor = (index) => {
    const colors = [
      'from-blue-500 to-cyan-500',
      'from-purple-500 to-pink-500',
      'from-orange-500 to-red-500'
    ];
    return colors[index] || 'from-gray-500 to-gray-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Sparkles className="w-12 h-12 text-purple-600 mr-3" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                AI Workflow Generator
              </h1>
              <p className="text-lg text-gray-600 mt-1">
                Describe your automation in plain English, get 3 AI-powered solutions
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors"
          >
            <Home className="w-5 h-5" />
            Dashboard
          </button>
        </div>

        {!workflowSuggestions.length ? (
          <>
            {/* Input Section */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe your workflow
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Example: Email me a summary of my Gmail messages every morning at 8am"
                className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                disabled={loading}
              />

              <button
                onClick={handleGenerate}
                disabled={loading || !description.trim()}
                className="mt-4 w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating 3 workflow suggestions...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Generate Workflow
                  </>
                )}
              </button>

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
            </div>

            {/* Example Prompts */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Try these examples:
              </h3>
              <div className="space-y-2">
                {examplePrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => setDescription(prompt)}
                    className="w-full text-left p-3 bg-gray-50 hover:bg-purple-50 rounded-lg transition-colors border border-gray-200 hover:border-purple-300"
                  >
                    <p className="text-sm text-gray-700">{prompt}</p>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* Workflow Suggestions Grid */
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Choose Your Approach
              </h2>
              <button
                onClick={() => {
                  setWorkflowSuggestions([]);
                  setSelectedWorkflow(null);
                  setDescription('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors"
              >
                Start Over
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {workflowSuggestions.map((workflow, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg shadow-lg overflow-hidden transition-all cursor-pointer ${
                    selectedWorkflow === workflow
                      ? 'ring-4 ring-purple-500 scale-105'
                      : 'hover:shadow-xl hover:scale-102'
                  }`}
                  onClick={() => handleSelectWorkflow(workflow)}
                >
                  {/* Card Header */}
                  <div className={`bg-gradient-to-r ${getApproachColor(index)} p-4 text-white`}>
                    <div className="flex items-center justify-between mb-2">
                      {getApproachIcon(index)}
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getConfidenceColor(workflow.confidence)} border`}>
                        {Math.round(workflow.confidence * 100)}% {getConfidenceLabel(workflow.confidence)}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold">
                      {workflow.title || `Approach ${index + 1}`}
                    </h3>
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    {/* Explanation */}
                    <div className="mb-4">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {workflow.explanation}
                      </p>
                    </div>

                    {/* Trigger */}
                    <div className="mb-3">
                      <div className="flex items-center mb-2">
                        <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                          <span className="text-purple-600 font-bold text-xs">⏰</span>
                        </div>
                        <h4 className="text-xs font-semibold text-gray-900 uppercase">Trigger</h4>
                      </div>
                      <div className="ml-8 text-xs text-gray-600">
                        {workflow.workflow.trigger.type === 'schedule' && 'Schedule: '}
                        {workflow.workflow.trigger.type === 'webhook' && 'Webhook: '}
                        {workflow.workflow.trigger.type === 'app_event' && `${workflow.workflow.trigger.app} Event: `}
                        <span className="text-purple-600 font-medium">
                          {workflow.workflow.trigger.schedule || workflow.workflow.trigger.event || 'Custom trigger'}
                        </span>
                      </div>
                    </div>

                    {/* Actions Count */}
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                          <span className="text-blue-600 font-bold text-xs">⚡</span>
                        </div>
                        <h4 className="text-xs font-semibold text-gray-900 uppercase">Actions</h4>
                      </div>
                      <div className="ml-8 text-xs text-gray-600">
                        {workflow.workflow.actions.length} step{workflow.workflow.actions.length !== 1 ? 's' : ''}
                        {workflow.workflow.actions.length > 0 && (
                          <span className="text-gray-500">
                            {' '}({workflow.workflow.actions.map(a => a.app).join(' → ')})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Clarifications */}
                    {workflow.clarifications_needed && workflow.clarifications_needed.length > 0 && (
                      <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                        ⚠️ {workflow.clarifications_needed.length} clarification{workflow.clarifications_needed.length !== 1 ? 's' : ''} needed
                      </div>
                    )}
                  </div>

                  {/* Card Actions */}
                  <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(workflow);
                      }}
                      className="flex-1 px-3 py-2 text-sm bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveDraft(workflow);
                      }}
                      className="flex-1 px-3 py-2 text-sm bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Workflow Details */}
            {selectedWorkflow && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Detailed View: {selectedWorkflow.title}
                </h3>

                {/* Actions Detail */}
                <div className="space-y-3">
                  {selectedWorkflow.workflow.actions.map((action, index) => (
                    <div key={index} className="relative">
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              Step {action.step}: {action.app}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              Action: <span className="text-blue-600">{action.action}</span>
                            </p>
                            {Object.keys(action.parameters || {}).length > 0 && (
                              <div className="mt-2 text-xs text-gray-500">
                                <p className="font-medium">Parameters:</p>
                                <pre className="mt-1 p-2 bg-white rounded border border-gray-200 overflow-x-auto">
                                  {JSON.stringify(action.parameters, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {index < selectedWorkflow.workflow.actions.length - 1 && (
                        <div className="flex justify-center py-2">
                          <div className="w-0.5 h-6 bg-gray-300"></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Clarifications Detail */}
                {selectedWorkflow.clarifications_needed && selectedWorkflow.clarifications_needed.length > 0 && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm font-semibold text-yellow-800 mb-2">
                      ⚠️ Clarifications Needed:
                    </p>
                    <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                      {selectedWorkflow.clarifications_needed.map((clarification, index) => (
                        <li key={index}>{clarification}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowGeneratorPage;
